/**
 * Abstract base class for broadcast channel communication
 * Provides type-safe messaging between browsing contexts (tabs, windows, iframes)
 * @template T The type of messages that will be sent/received through the channel
 */
export abstract class BroadcastBaseService<T = unknown> {
    protected readonly broadcastChannel: BroadcastChannel;
    private messageHandler?: (event: MessageEvent<T>) => void;
    private isChannelClosed = false;

    /**
     * Creates a new broadcast channel service
     * @param channelName Unique identifier for the broadcast channel
     */
    constructor(channelName: string) {
        this.broadcastChannel = new BroadcastChannel(channelName);
        this.setupMessageListener();
    }

    /**
     * Sets up the message listener for the broadcast channel
     */
    private setupMessageListener(): void {
        this.messageHandler = (event: MessageEvent<T>) => {
            this.onMessage(event.data);
        };
        this.broadcastChannel.addEventListener('message', this.messageHandler);
    }

    /**
     * Abstract method to handle incoming messages
     * Must be implemented by derived classes
     * @param message The received message
     */
    protected abstract onMessage(message: T): void;

    /**
     * Sends a message to all listeners on the broadcast channel
     * @param message The message to broadcast
     * @throws Error if channel is closed or message cannot be sent
     */
    public sendMessage(message: T): void {
        if (this.isChannelClosed) {
            throw new Error('[Broadcast] Cannot send message: broadcast channel is closed');
        }

        try {
            this.broadcastChannel.postMessage(message);
        } catch (error) {
            console.error('Failed to send broadcast message:', error);
            throw error;
        }
    }

    /**
     * Closes the broadcast channel and removes event listeners
     * Safe to call multiple times
     */
    public closeChannel(): void {
        if (this.isChannelClosed) {
            return;
        }

        if (this.messageHandler) {
            this.broadcastChannel.removeEventListener('message', this.messageHandler);
            this.messageHandler = undefined;
        }

        this.broadcastChannel.close();
        this.isChannelClosed = true;
    }

    /**
     * Checks if the broadcast channel is currently open
     * @returns true if the channel is open, false otherwise
     */
    public isOpen(): boolean {
        return !this.isChannelClosed;
    }

    /**
     * Gets the channel name
     * Note: BroadcastChannel API doesn't expose name property directly
     */
    protected getChannelName(): string {
        return this.broadcastChannel.name || 'unknown';
    }
}
